import { api } from "@/common/http/axios";

/** 模型管理 API 客户端。设计文档: docs/model-management-schema-v0.3.md
 * MVP 阶段只覆盖读侧;upload / patch 待后续接口就绪补齐。 */

// ============================================================
// model 表
// ============================================================

export interface ModelRow {
  id: number;
  name: string;
  version: string;
  tag: string;
  category: string | null;
  type: string | null;
  framework: string | null;
  storage_uri: string;
  file_name: string;
  file_size: number;
  file_sha256: string;
  description: string | null;
  meta: Record<string, unknown> | null;
  uploader: string;
  create_time: string;
  update_time: string;
  active: boolean;
}

/** 列表页每行 (name 聚合视图)。 */
export interface ModelListItem {
  name: string;
  category: string | null;
  type: string | null;
  current_version: string;
  tags: string[];
  created: string;
  updated: string;
  active_versions: number;
  total_versions: number;
  all_inactive: boolean;
  /** 可用版本文件的框架（onnx / tensorrt ...），未填写的不计入 */
  frameworks?: string[];
  /** 最近一次上传时间（停用 / 启用不影响） */
  last_uploaded?: string | null;
}

export interface ModelListResponse {
  items: ModelListItem[];
  total: number;
}

/** 每个 version 一个块;展开后是 tag 列表。 */
export interface ModelHistoryEntry {
  version: string;
  tags: ModelRow[];
  active: boolean;
  is_current: boolean;
  latest_time: string;
}

export interface ModelDetail {
  name: string;
  category: string | null;
  type: string | null;
  framework: string | null;
  current_version: string | null;
  total_versions: number;
  active_versions: number;
  first_uploaded_at: string;
  first_uploaded_by: string;
  last_updated_at: string;
  last_updated_by: string;
  description: string | null;
  history: ModelHistoryEntry[];
}

export interface ListModelsParams {
  category?: string;
  type?: string;
  include_all_inactive?: boolean;
}

export const listModels = async (
  params: ListModelsParams = {},
): Promise<ModelListResponse> => {
  const response = await api.get("/model/list", { params });
  return response.data?.data as ModelListResponse;
};

export const getModelDetail = async (
  name: string,
): Promise<ModelDetail | null> => {
  const response = await api.get(`/model/detail/${encodeURIComponent(name)}`);
  return response.data?.data as ModelDetail | null;
};

/** 元数据字段更新;字段=undefined 表示不动。active 不能走这里。 */
export interface ModelPatchRequest {
  description?: string | null;
  meta?: Record<string, unknown> | null;
  category?: string | null;
  type?: string | null;
}

export const patchModel = async (
  modelId: number,
  patch: ModelPatchRequest,
): Promise<ModelRow> => {
  const response = await api.patch(`/model/${modelId}`, patch);
  return response.data?.data as ModelRow;
};

export const activateModel = async (modelId: number): Promise<ModelRow> => {
  const response = await api.post(`/model/${modelId}:activate`);
  return response.data?.data as ModelRow;
};

export const deactivateModel = async (modelId: number): Promise<ModelRow> => {
  const response = await api.post(`/model/${modelId}:deactivate`);
  return response.data?.data as ModelRow;
};

/** 上传一个 (name, version, tag) 文件到 BOS + 落 model 表。 */
export interface UploadModelParams {
  name: string;
  version: string;
  tag: string;
  file: File;
  uploader: string;
  category?: string;
  type?: string;
  framework?: string;
  description?: string;
  meta?: Record<string, unknown>;
  /** 一次上传的批次号：同一版本的多个文件带同一个值（同一模型类别下版本号不能重复） */
  upload_batch?: string;
}

export const uploadModel = async (p: UploadModelParams): Promise<ModelRow> => {
  const form = new FormData();
  form.append("file", p.file);
  form.append("name", p.name);
  form.append("version", p.version);
  form.append("tag", p.tag);
  if (p.upload_batch) form.append("upload_batch", p.upload_batch);
  if (p.framework) form.append("framework", p.framework);
  form.append("uploader", p.uploader);
  if (p.category) form.append("category", p.category);
  if (p.type) form.append("type", p.type);
  if (p.description) form.append("description", p.description);
  if (p.meta) form.append("meta", JSON.stringify(p.meta));
  const response = await api.post("/model/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data?.data as ModelRow;
};

export interface BuildReleaseParams {
  major_group: string;
  created_by: string;
  kind?: "release" | "dev";
  release_name?: string;
  release_seg?: string;
  category?: string;
  based_on?: string;
  description?: string;
}

export const buildRelease = async (
  p: BuildReleaseParams,
): Promise<ModelReleaseDetail> => {
  const form = new FormData();
  Object.entries(p).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") form.append(k, String(v));
  });
  const response = await api.post("/model/release", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data?.data as ModelReleaseDetail;
};

/** 追加/替换一批模型到已有基版的 manifest.items, 不新建 release 行。
 *  UI 「关闭生成新版本号」开关时走这条: 把新上传的模型绑定到指定基版。
 */
export interface AttachToReleaseItem {
  name: string;
  version: string;
  category?: string | null;
  type?: string | null;
  artifacts: Record<string, {
    model_id?: number;
    storage_uri?: string;
    file_sha256?: string;
    file_size?: number;
  }>;
}

export const attachToRelease = async (
  releaseName: string,
  items: AttachToReleaseItem[],
  updatedBy: string,
): Promise<ModelReleaseDetail> => {
  const form = new FormData();
  form.append("updated_by", updatedBy);
  form.append("items", JSON.stringify(items));
  const response = await api.post(
    `/model/release/${encodeURIComponent(releaseName)}/attach`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return response.data?.data as ModelReleaseDetail;
};

/** 允许上传的模型类别（系统配置，管理员维护）；模型上传页下拉只列这些。 */
export const getModelUploadNames = async (): Promise<string[]> => {
  const response = await api.get("/model/upload-names");
  return (response.data?.data ?? []) as string[];
};

// ============================================================
// 模型分类 / 模型类别（design model-management-revamp §4、§6）
// ============================================================

/** {一级分类: {label 中文名, types 二级分类}}；一级分类固定，二级分类由管理员在系统配置维护。 */
/** type_labels：二级分类中文名（与中台页面上的模型类型名一致），只用于展示，可缺省。 */
export type ModelCategories = Record<string, { label: string; types: string[]; type_labels?: Record<string, string> }>;

export const getModelCategories = async (): Promise<ModelCategories> => {
  const response = await api.get("/model/categories");
  return response.data?.data as ModelCategories;
};

/** 已登记的模型类别：一个名称一个安装目录；同一目录按二级分类区分（目录 + 二级分类唯一）。 */
export interface ModelNameVo {
  name: string;
  category: string;
  type: string | null;
  /** 二级分类中文名（如「视觉车道线检测」），没配为 null */
  type_label?: string | null;
  install_dir: string;
  description: string | null;
  status: "enabled" | "disabled";
  created_by: string;
  create_time: string;
  update_time: string;
  /** 仿真端按目录认出的模块（Prediction / PointPillarDetection / TrafficLightDetector），认不出为 null */
  runtime_module: string | null;
  latest_version: string | null;
  active_versions: number;
}

export const listModelNames = async (
  params: { category?: string; type?: string; include_disabled?: boolean } = {},
): Promise<ModelNameVo[]> => {
  const response = await api.get("/model/names", { params });
  return (response.data?.data ?? []) as ModelNameVo[];
};

/** model 表里有、还没登记安装目录的名称（附平台推断的建议值）。 */
export interface UnregisteredModelName {
  name: string;
  suggested_category: string | null;
  suggested_type: string | null;
  suggested_install_dir: string | null;
  suggestion_source: string;
  active_versions: number;
  latest_version: string;
  latest_uploader: string;
  latest_time: string;
  /** 建议目录已被哪个已登记名称占用 */
  dir_taken_by: string | null;
  /** 还有哪些未登记名称推断出同一目录 */
  dir_claimed_by: string[];
}

export const listUnregisteredModelNames = async (): Promise<UnregisteredModelName[]> => {
  const response = await api.get("/model/names:unregistered");
  return (response.data?.data ?? []) as UnregisteredModelName[];
};

export interface ModelNameCreate {
  name: string;
  category: string;
  type?: string | null;
  install_dir: string;
  description?: string | null;
}

export const createModelName = async (p: ModelNameCreate): Promise<ModelNameVo> => {
  const response = await api.post("/model/names", p);
  return response.data?.data as ModelNameVo;
};

/** 管理员修改；未传字段不变。 */
export interface ModelNamePatch {
  category?: string;
  type?: string | null;
  clear_type?: boolean;
  install_dir?: string;
  description?: string;
  status?: "enabled" | "disabled";
}

export const patchModelName = async (name: string, p: ModelNamePatch): Promise<ModelNameVo> => {
  const response = await api.patch(`/model/names/${encodeURIComponent(name)}`, p);
  return response.data?.data as ModelNameVo;
};

/** 按版本停用 / 启用（本人上传或管理员）：只影响之后新建的主版本。 */
export const setModelVersionActive = async (
  name: string, version: string, active: boolean,
): Promise<{ name: string; version: string; changed: number }> => {
  const op = active ? "activate" : "deactivate";
  const response = await api.post(
    `/model/names/${encodeURIComponent(name)}/versions/${encodeURIComponent(version)}:${op}`,
  );
  return response.data?.data;
};

// ============================================================
// 主版本 = 基版 + 改动（design model-management-revamp §5）
// ============================================================

export type ReleaseChangeOp = "replace" | "add" | "remove";

export interface ReleaseChange {
  op: ReleaseChangeOp;
  name: string;
  from: string | null;
  to: string | null;
}

export const getLatestRelease = async (
  kind: "release" | "dev" = "release",
  /** 用作默认基版：跳过「中台同步 + 不使用基版」生成的、只含中台模型的主版本 */
  asBase = false,
): Promise<{ name: string; create_time: string } | null> => {
  const response = await api.get("/model/releases:latest", { params: { kind, ...(asBase ? { asBase: true } : {}) } });
  return response.data?.data ?? null;
};

export const getReleaseChanges = async (name: string): Promise<ReleaseChange[]> => {
  const response = await api.get(`/model/release/${encodeURIComponent(name)}/changes`);
  return (response.data?.data ?? []) as ReleaseChange[];
};

// ============================================================
// model_release 表 (主版本)
// ============================================================

export interface ModelReleaseRow {
  id: number;
  name: string;
  category: string | null;
  kind: "release" | "dev";
  major_group: string;
  release_seg: string | null;
  based_on: string | null;
  trigger_type: string;
  /** 中台同步生成的主版本对应的中台发版名称（如「主线感知模型V91」），其它来源为 null */
  ndp_release_name?: string | null;
  description: string | null;
  created_by: string;
  create_time: string;
}

export interface ModelReleaseDetail extends ModelReleaseRow {
  manifest: {
    name?: string;
    based_on?: string | null;
    items: Array<{
      name: string;
      version: string;
      category?: string;
      type?: string;
      /** 创建时从 model_name 冻结；旧主版本没有 */
      install_dir?: string;
      artifacts: Record<
        string,
        { model_id: number; storage_uri: string; file_sha256: string; file_size: number; file_name?: string | null }
      >;
    }>;
    /** 相对基版的改动（新主版本才有） */
    changes?: Array<{ op: "replace" | "add" | "remove"; name: string; from: string | null; to: string | null }>;
  };
  trigger_ref: Record<string, unknown> | null;
}

export interface MajorGroupTree {
  major_group: string;
  kind: "release" | "dev";
  release_count: number;
  latest_time: string;
  releases: ModelReleaseRow[];
}

export interface ModelReleaseTreeResponse {
  groups: MajorGroupTree[];
}

export const listReleaseTree = async (
  kind?: "release" | "dev",
): Promise<ModelReleaseTreeResponse> => {
  const response = await api.get("/model/release/tree", {
    params: kind ? { kind } : undefined,
  });
  return response.data?.data as ModelReleaseTreeResponse;
};

export const getRelease = async (
  name: string,
): Promise<ModelReleaseDetail | null> => {
  const response = await api.get(`/model/release/${encodeURIComponent(name)}`);
  return response.data?.data as ModelReleaseDetail | null;
};
